import {
  type ServiceGroup,
  type ServiceHealthState,
  type ServiceIncident,
  type ServiceStatusItem,
} from '@utazzo/shared';
import { UzBadge, UzButton, UzCard, UzSpinner, UzTextarea } from '@utazzo/ui';
import { Activity, RefreshCw, X } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';

import {
  IncidentRow,
  STATE_LABEL,
  STATE_TONE,
  ServiceCard,
  StateDot,
  UptimeBar,
  formatDuration,
  formatIst,
} from '../components/StatusWidgets';
import {
  useRunStatusChecks,
  useServiceHistory,
  useSetIncidentNote,
  useStatusIncidents,
  useStatusOverview,
} from '../use-status';

const GROUP_ORDER: ServiceGroup[] = ['APP', 'EXTERNAL', 'PROVIDER', 'INFRA'];
const GROUP_LABEL: Record<ServiceGroup, string> = {
  APP: 'Our apps',
  EXTERNAL: 'Seen from outside AWS',
  PROVIDER: 'Third-party integrations',
  INFRA: 'Infrastructure',
};
const GROUP_HINT: Record<ServiceGroup, string> = {
  APP: 'Probed from the API every minute.',
  EXTERNAL: 'Probed every minute by the status watcher Lambda, so an API outage is recorded too.',
  PROVIDER: 'Probed every 5 minutes with read-only calls.',
  INFRA: 'Probed from the API every minute.',
};

/**
 * Admin-only status page (docs/ops/STATUS-PAGE.md): one card per watched
 * thing, a 90-day strip and the incident log with duration, what the
 * probe saw, and the admin's own note.
 */
export const StatusPage = (): React.JSX.Element => {
  const overview = useStatusOverview();
  const incidents = useStatusIncidents({ limit: 100 });
  const runChecks = useRunStatusChecks();
  const setNote = useSetIncidentNote();
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const history = useServiceHistory(selectedKey);
  const [editing, setEditing] = useState<ServiceIncident | null>(null);
  const [noteDraft, setNoteDraft] = useState('');

  const grouped = useMemo(() => {
    const map = new Map<ServiceGroup, ServiceStatusItem[]>();
    for (const s of overview.data?.services ?? []) {
      const list = map.get(s.group) ?? [];
      list.push(s);
      map.set(s.group, list);
    }
    for (const list of map.values()) {
      list.sort((a, b) => rank(b.status) - rank(a.status) || a.name.localeCompare(b.name));
    }
    return map;
  }, [overview.data]);

  const overall: ServiceHealthState = overview.data?.overall ?? 'UNKNOWN';
  const openIncidents = (overview.data?.services ?? []).filter((s) => s.openIncident);

  const onCheckNow = () => {
    runChecks.mutate(
      { group: 'all' },
      {
        onSuccess: (r) =>
          toast.success(
            `Checked ${r.checked} service(s)` +
              (r.opened.length ? `, ${r.opened.length} new incident(s)` : '') +
              (r.resolved.length ? `, ${r.resolved.length} resolved` : ''),
          ),
        onError: () => toast.error('Could not run the checks.'),
      },
    );
  };

  const saveNote = () => {
    if (!editing) return;
    setNote.mutate(
      { id: editing.id, note: noteDraft },
      {
        onSuccess: () => {
          toast.success('Note saved');
          setEditing(null);
        },
        onError: () => toast.error('Could not save the note.'),
      },
    );
  };

  return (
    <div className="space-y-6 p-4 md:p-6">
      <header className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-3">
          <StateDot state={overall} className="h-3.5 w-3.5" />
          <div>
            <h1 className="text-xl font-semibold">
              {overall === 'UP'
                ? 'All systems operational'
                : overall === 'DOWN'
                  ? 'Something is down'
                  : overall === 'DEGRADED'
                    ? 'Some services are degraded'
                    : 'Status'}
            </h1>
            <p className="text-muted-foreground text-xs">
              {overview.data ? `Updated ${formatIst(overview.data.generatedAt)} IST` : 'Loading'}
              {' | '}
              {openIncidents.length} open incident(s)
            </p>
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <UzButton
            variant="outline"
            size="sm"
            leftIcon={<RefreshCw className="h-3.5 w-3.5" />}
            onClick={() => {
              void overview.refetch();
              void incidents.refetch();
            }}
          >
            Refresh
          </UzButton>
          <UzButton
            size="sm"
            leftIcon={<Activity className="h-3.5 w-3.5" />}
            loading={runChecks.isPending}
            onClick={onCheckNow}
          >
            Check now
          </UzButton>
        </div>
      </header>

      {overview.isLoading ? (
        <div className="flex justify-center py-12">
          <UzSpinner />
        </div>
      ) : overview.isError ? (
        <UzCard>
          <p className="text-sm text-red-700">Could not load the status overview.</p>
        </UzCard>
      ) : (
        GROUP_ORDER.filter((g) => grouped.has(g)).map((g) => (
          <section key={g} className="space-y-2">
            <div className="flex items-baseline gap-2">
              <h2 className="text-muted-foreground text-sm font-semibold tracking-wide uppercase">
                {GROUP_LABEL[g]}
              </h2>
              <span className="text-muted-foreground text-xs">{GROUP_HINT[g]}</span>
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
              {grouped.get(g)!.map((item) => (
                <ServiceCard
                  key={item.key}
                  item={item}
                  selected={selectedKey === item.key}
                  onSelect={(k) => setSelectedKey((cur) => (cur === k ? null : k))}
                />
              ))}
            </div>
          </section>
        ))
      )}

      {selectedKey ? (
        <UzCard
          title={history.data?.name ?? selectedKey}
          description="Last 90 days. Each cell is one day in IST; hover for the detail."
          actions={
            <button
              type="button"
              className="hover:bg-muted rounded p-1"
              onClick={() => setSelectedKey(null)}
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          }
        >
          {history.isLoading ? (
            <UzSpinner />
          ) : history.data ? (
            <div className="space-y-4">
              <UptimeBar days={history.data.days} />
              <div className="text-muted-foreground flex gap-4 text-xs">
                <span>
                  {history.data.incidents.length} incident(s) in 90 days,{' '}
                  {formatDuration(
                    history.data.incidents.reduce((n, i) => n + i.durationSeconds, 0),
                  )}{' '}
                  affected
                </span>
                <span>{history.data.samples.length} probe(s) in the last 24 h</span>
              </div>
              <Samples24h samples={history.data.samples} />
              {history.data.incidents.length > 0 ? (
                <IncidentsTable
                  incidents={history.data.incidents}
                  onEditNote={(i) => openEditor(i)}
                />
              ) : (
                <p className="text-muted-foreground text-sm">
                  No incidents recorded for this service.
                </p>
              )}
            </div>
          ) : null}
        </UzCard>
      ) : null}

      <UzCard
        title="Incident log"
        description="Every outage or degradation the probes recorded: when, for how long, what was seen, and your note on why."
      >
        {incidents.isLoading ? (
          <UzSpinner />
        ) : incidents.data && incidents.data.items.length > 0 ? (
          <IncidentsTable incidents={incidents.data.items} onEditNote={(i) => openEditor(i)} />
        ) : (
          <p className="text-muted-foreground text-sm">No incidents yet.</p>
        )}
      </UzCard>

      {editing ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-background w-full max-w-lg rounded-lg p-4 shadow-lg">
            <div className="mb-2 flex items-center gap-2">
              <UzBadge tone={STATE_TONE[editing.severity]} pill>
                {editing.severity}
              </UzBadge>
              <span className="text-sm font-medium">{editing.serviceName}</span>
              <span className="text-muted-foreground text-xs">
                {formatIst(editing.startedAt)} IST, {formatDuration(editing.durationSeconds)}
              </span>
            </div>
            <p className="text-muted-foreground mb-3 text-xs">Seen: {editing.cause}</p>
            <UzTextarea
              label="Root cause / follow-up note"
              rows={4}
              value={noteDraft}
              onChange={(e) => setNoteDraft(e.target.value)}
              placeholder="What actually happened and what was done, e.g. EC2 reboot paused the DoubleTick webhook; re-saved it."
            />
            <div className="mt-3 flex justify-end gap-2">
              <UzButton variant="outline" size="sm" onClick={() => setEditing(null)}>
                Cancel
              </UzButton>
              <UzButton size="sm" loading={setNote.isPending} onClick={saveNote}>
                Save note
              </UzButton>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );

  function openEditor(i: ServiceIncident): void {
    setEditing(i);
    setNoteDraft(i.note ?? '');
  }
};

const rank = (s: ServiceHealthState): number =>
  s === 'DOWN' ? 3 : s === 'DEGRADED' ? 2 : s === 'UNKNOWN' ? 1 : 0;

const IncidentsTable = ({
  incidents,
  onEditNote,
}: {
  incidents: ServiceIncident[];
  onEditNote: (i: ServiceIncident) => void;
}) => (
  <div className="overflow-x-auto">
    <table className="w-full text-left">
      <thead>
        <tr className="text-muted-foreground border-b text-xs tracking-wide uppercase">
          <th className="px-3 py-2">Service</th>
          <th className="px-3 py-2">When (IST)</th>
          <th className="px-3 py-2">Duration</th>
          <th className="px-3 py-2">What the probe saw</th>
          <th className="px-3 py-2">Note</th>
        </tr>
      </thead>
      <tbody>
        {incidents.map((i) => (
          <IncidentRow key={i.id} incident={i} onEditNote={onEditNote} />
        ))}
      </tbody>
    </table>
  </div>
);

/** Last 24 h of raw probes as a thin strip: one cell per sample, red where it failed. */
const Samples24h = ({
  samples,
}: {
  samples: Array<{
    at: string;
    status: ServiceHealthState;
    latencyMs: number | null;
    error: string | null;
  }>;
}) => {
  if (samples.length === 0) return null;
  return (
    <div>
      <p className="text-muted-foreground mb-1 text-xs">Last 24 h, one cell per probe</p>
      <div className="flex h-3 gap-px">
        {samples.map((s) => (
          <div
            key={s.at}
            title={`${formatIst(s.at)} IST: ${STATE_LABEL[s.status]}${s.latencyMs !== null ? `, ${s.latencyMs} ms` : ''}${
              s.error ? ` - ${s.error}` : ''
            }`}
            className={
              'flex-1 rounded-[1px] ' +
              (s.status === 'UP'
                ? 'bg-emerald-400'
                : s.status === 'DEGRADED'
                  ? 'bg-amber-400'
                  : s.status === 'DOWN'
                    ? 'bg-red-500'
                    : 'bg-slate-200')
            }
          />
        ))}
      </div>
    </div>
  );
};
